import { useState } from "react";
import packageJson from "../../package.json";
import { getFeedbackFormAction } from "../content/feedbackConfig";
import { useLocale } from "../i18n/useLocale";

const FEEDBACK_TYPES = [
  { value: "bug", labelKey: "web.feedback.typeBug" },
  { value: "idea", labelKey: "web.feedback.typeIdea" },
  { value: "question", labelKey: "web.feedback.typeQuestion" },
  { value: "other", labelKey: "web.feedback.typeOther" },
];

const INITIAL_FORM = {
  type: "idea",
  message: "",
  email: "",
};

export function FeedbackForm() {
  const { display } = useLocale();
  const [form, setForm] = useState(INITIAL_FORM);
  const [status, setStatus] = useState("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const formAction = getFeedbackFormAction();

  const update = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    if (status === "error") {
      setStatus("idle");
      setErrorMessage("");
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!formAction) {
      setStatus("error");
      setErrorMessage(display.t("web.feedback.errorUnavailable"));
      return;
    }

    if (!form.message.trim()) {
      setStatus("error");
      setErrorMessage(display.t("web.feedback.errorEmpty"));
      return;
    }

    setStatus("sending");
    setErrorMessage("");

    const body = new FormData();
    body.append("type", form.type);
    body.append("message", form.message.trim());
    if (form.email.trim()) {
      body.append("email", form.email.trim());
      body.append("_replyto", form.email.trim());
    }
    body.append("source", "web");
    body.append("page", window.location.href);
    body.append("app_version", packageJson.version);
    const typeLabel = FEEDBACK_TYPES.find((item) => item.value === form.type);
    body.append(
      "_subject",
      `${display.t("web.seo.feedbackSubjectPrefix")}: ${typeLabel ? display.t(typeLabel.labelKey) : form.type}`,
    );

    try {
      const response = await fetch(formAction, {
        method: "POST",
        body,
        headers: { Accept: "application/json" },
      });

      if (!response.ok) {
        throw new Error("send_failed");
      }

      setForm(INITIAL_FORM);
      setStatus("success");
    } catch {
      setStatus("error");
      setErrorMessage(display.t("web.feedback.errorSendFailed"));
    }
  };

  const messageErrorId = "feedback-message-error";
  const hasMessageError = status === "error" && errorMessage === display.t("web.feedback.errorEmpty");

  if (status === "success") {
    return (
      <div className="feedback-form-success" role="status">
        <strong>{display.t("web.feedback.successTitle")}</strong>
        <p>{display.t("web.feedback.successBody")}</p>
        <button
          type="button"
          className="secondary-button"
          onClick={() => setStatus("idle")}
        >
          {display.t("web.feedback.sendAnother")}
        </button>
      </div>
    );
  }

  return (
    <form className="feedback-form" onSubmit={handleSubmit} noValidate>
      <label>
        <span>{display.t("web.feedback.topicLabel")}</span>
        <select
          value={form.type}
          onChange={(event) => update("type", event.target.value)}
          disabled={status === "sending"}
        >
          {FEEDBACK_TYPES.map((item) => (
            <option key={item.value} value={item.value}>{display.t(item.labelKey)}</option>
          ))}
        </select>
      </label>

      <label>
        <span>{display.t("web.feedback.messageLabel")}</span>
        <textarea
          value={form.message}
          onChange={(event) => update("message", event.target.value)}
          rows={5}
          required
          disabled={status === "sending"}
          placeholder={display.t("web.feedback.messagePlaceholder")}
          aria-invalid={hasMessageError || undefined}
          aria-describedby={status === "error" && errorMessage ? messageErrorId : undefined}
        />
      </label>

      <label>
        <span>{display.t("web.feedback.emailLabel")}</span>
        <input
          type="email"
          value={form.email}
          onChange={(event) => update("email", event.target.value)}
          disabled={status === "sending"}
          placeholder={display.t("web.feedback.emailPlaceholder")}
          autoComplete="email"
        />
      </label>

      <p className="feedback-form-note">
        {display.t("web.feedback.privacyNote")}
      </p>

      {status === "error" && errorMessage && (
        <p className="feedback-form-error" id={messageErrorId} role="alert">{errorMessage}</p>
      )}

      <button
        type="submit"
        className="primary-button"
        disabled={status === "sending" || !formAction}
      >
        {status === "sending"
          ? display.t("web.feedback.submitting")
          : display.t("web.feedback.submit")}
      </button>
    </form>
  );
}
